import type { QiraMessage } from 'api3';
import { Injectable, signal } from '@angular/core';
import { environment } from '@env';
import * as Sentry from '@sentry/angular';

// Chrome browser on iOS
// Maybe not available on all devices ?!
enum GermanBrowserVoices {
  Anne = 'Anne',
  Eddy = 'Eddy',
  Flo = 'Flo',
  Grandma = 'Grandma',
  Grandpa = 'Grandpa',
  Helena = 'Helena',
  Martin = 'Martin',
  Reed = 'Reed',
  Rocko = 'Rocko',
  Sandy = 'Sandy',
  Shelly = 'Shelly',
  Google = 'Google',
}

@Injectable({
  providedIn: 'root',
})
export class VoiceService {
  static apiUrl = `${environment.api.url}`;

  voices: SpeechSynthesisVoice[] = [];
  audio: HTMLAudioElement | undefined;
  browserAudio: SpeechSynthesis | undefined;

  audioEnabled = signal(false);
  isPlaying = signal(false);

  private loadVoices(): void {
    if (!('speechSynthesis' in window)) return;

    window.speechSynthesis.onvoiceschanged = () => {
      this.voices = window.speechSynthesis.getVoices();
    };
  }

  constructor() {
    this.loadVoices();
    if ('speechSynthesis' in window) {
      this.browserAudio = window.speechSynthesis;
    }
  }

  private getVoice(voiceName: GermanBrowserVoices = GermanBrowserVoices.Anne): SpeechSynthesisVoice | undefined {
    const lang = 'de-DE';

    const foundVoice = this.voices.find((voice) => voice.lang === lang && voice.name.startsWith(voiceName));
    if (foundVoice) return foundVoice;

    // if the names are just a chrome thing, take any one
    return this.voices.find((voice) => voice.lang === lang);
  }

  public readOutLoud(message: QiraMessage): void {
    if (!this.audioEnabled()) return;
    if (!message) return;

    this.isPlaying.set(true);

    this.readOutWithQiraVoice(message.id);
    // this.readOutViaBrowser(message.text);
  }

  private readOutViaBrowser(text: string): void {
    if (!this.browserAudio) return;
    if (!text) return;

    this.browserAudio.cancel();

    const speech = new SpeechSynthesisUtterance();
    speech.text = text;
    speech.volume = 1;
    speech.rate = 1;
    speech.pitch = 1;
    const voice = this.getVoice(GermanBrowserVoices.Helena);
    if (voice) speech.voice = voice;
    speech.onend = () => {
      console.log('[Browser speach] Speech ended');
      this.isPlaying.set(false);
    };

    this.browserAudio.speak(speech);
  }

  private readOutWithQiraVoice(messageId: string): void {
    if (this.audio) {
      this.audio.pause();
      this.audio = undefined;
    }

    this.audio = new Audio();
    this.audio.src = `${VoiceService.apiUrl}/voice/stream/${messageId}`;
    this.audio.onerror = (e) => {
      console.error('[VoiceService] Audio error:', e);
      Sentry.captureException(e, {
        tags: {
          location: 'VoiceService',
          feature: 'VoiceOutput',
          message: 'Load voice stream failed.',
        },
      });
    };
    this.audio.onended = () => {
      console.log('[Qira speach] Speech ended');
    };

    this.audio.play().catch((error) => {
      console.error('[VoiceService] Playback failed:', error);
      Sentry.captureException(error, {
        tags: {
          location: 'VoiceService',
          feature: 'VoiceOutput',
          message: 'Playback failed.',
        },
      });
    });
  }

  public toggleAudio(): void {
    const nowPlaying = this.isPlaying();
    if (nowPlaying) {
      // pause
      this.audio?.pause();
      this.browserAudio?.pause();
      this.isPlaying.set(false);
    } else {
      // resume
      this.audio?.play();
      this.browserAudio?.resume();
      this.isPlaying.set(true);
    }
  }

  public stopAudio() {
    if (this.audio) {
      this.audio.pause();
      this.audio.src = '';
      this.audio = undefined;
    }
    if (this.browserAudio) {
      this.browserAudio.cancel();
    }
  }
}
